import { getWeek } from "date-fns";

import {
	add,
	format as formatFns,
	isSameDay as isSameDayFns,
	isAfter as isAfterFns,
	isBefore as isBeforeFns,
	setHours,
	setMinutes,
	isFirstDayOfMonth,
	isToday,
	isTomorrow,
	isYesterday,
	intervalToDuration,
	interval as intervalFns,
	Interval,
	addMinutes as addMinutesFns,
	Duration,
	getWeekYear,
} from "date-fns";
import { getUserLocale } from "./locale-helper";
import { capitalize } from "./string-helper";

export const formatDateString = (date: Date, formatString: string) => {
	const locale = getUserLocale();

	// Special case for Swedish locale to ensure month is always abbreviated to three letters
	if (formatString === "MMM") {
		return formatFns(date, formatString, { locale: locale.locale }).substring(0, 3);
	}

	// Special case for Danish locale to remove dot from weekday abbreviation
	if (formatString === "EEE" && locale.localeString === "da-DK") {
		return formatFns(date, formatString, { locale: locale.locale }).substring(0, 3);
	}

	// Special case for Norwegian locale to ensure dot after date
	if ((formatString === "d" && locale.localeString === "nb-NO") || locale.localeString === "da-DK") {
		return `${formatFns(date, formatString, { locale: locale.locale })}`;
	}
	return formatFns(date, formatString, { locale: locale.locale });
};

/**
 * Formats date to local string
 * @param date Date to format.
 * @param shouldCapitalizeFirstLetter should capitalize first letter of day, defaults to true
 */
export const formatDateByLocale = (
	date: Date,
	dateStyle: "full" | "medium" | "short",
	shouldCapitalizeFirstLetter: boolean = true,
) => {
	const intlDate = new Intl.DateTimeFormat(getUserLocale().localeString, {
		dateStyle: dateStyle,
	}).format(date);

	if (shouldCapitalizeFirstLetter) return capitalize(intlDate);
	return intlDate;
};

/**
 * Format date to local string using 'today/tomorrow/yesterday'
 * @param date Date to format.
 * @param shouldCapitalizeFirstLetter should capitalize first letter of day, defaults to true
 */
export const formatDateByLocalRelative = (
	date: Date,
	dateStyle: "full" | "medium" | "short",
	shouldCapitalizeFirstLetter: boolean = true,
) => {
	const rtf = new Intl.RelativeTimeFormat(getUserLocale().localeString, { numeric: "auto" });
	let result: string;

	if (isToday(date)) {
		result = rtf.formatToParts(0, "day")[0].value;
	} else if (isTomorrow(date)) {
		result = rtf.formatToParts(1, "day")[0].value;
	} else if (isYesterday(date)) {
		result = rtf.formatToParts(-1, "day")[0].value;
	} else {
		result = formatDateByLocale(date, dateStyle);
	}

	if (shouldCapitalizeFirstLetter) return capitalize(result);

	return result;
};

/**
 * Formats time to local string
 * @param date Date to format.
 */
export const formatTimeByLocale = (date: Date | undefined) => {
	if (!date) {
		return "";
	}
	const intlDate = new Intl.DateTimeFormat(getUserLocale().localeString, {
		timeStyle: "short",
	}).format(date);
	return intlDate;
};

export const addTime = (date: Date, duration: Duration) => {
	return add(date, duration);
};

export const durationBetweenDates = (dateLeft: Date, dateRight: Date, ensurePositiveResult?: boolean) => {
	let interval: Interval;

	if (ensurePositiveResult && dateLeft > dateRight) {
		interval = intervalFns(dateRight, dateLeft);
	} else {
		interval = intervalFns(dateLeft, dateRight);
	}

	return intervalToDuration(interval);
};

export const formatDate = (date?: Date): string => {
	return date ? formatDateString(date, "HH:mm") : "";
};

export const isTodayOrAfter = (date: Date) => {
	const today = new Date();
	return isSameDayFns(date, today) || isAfterFns(date, today);
};

export const isTodayOrBefore = (date: Date) => {
	const today = new Date();
	return isSameDayFns(date, today) || isBeforeFns(date, today);
};

export const setTimeOfDateWithString = (date: Date, time: string) => {
	const [hours, minutes] = time.split(":");
	return setTimeOfDate(date, +hours, +minutes);
};

export const setTimeOfDate = (date: Date, hours: number, minutes: number) => {
	date = setHours(date, hours);
	date = setMinutes(date, minutes);
	return date;
};

export const addMunutes = (date: Date, minutes: number) => {
	date = addMinutesFns(date, minutes);
	return date;
};

export const isBefore = (date: Date, dateToCompare: Date) => {
	return isBeforeFns(date, dateToCompare);
};

const firstDayOfMonth = (date: Date) => {
	const copy = new Date(date);

	copy.setDate(1);
	copy.setHours(0);
	copy.setMinutes(0);
	copy.setSeconds(0);
	copy.setMilliseconds(0);

	return copy;
};

//If todays date is 1:st of a month, then show previous month
export const getDateForTimereport = () => {
	let date = new Date();
	date = isFirstDayOfMonth(date) ? add(date, { months: -1 }) : date;
	return firstDayOfMonth(date);
};

export enum DayOfWeek {
	Sunday = 0,
	Monday = 1,
	Tuesday = 2,
	Wednesday = 3,
	Thursday = 4,
	Friday = 5,
	Saturday = 6,
}
export const getDayOfWeekString = (dayOfWeek: DayOfWeek, format: "long" | "short" = "long"): string => {
	const date = new Date(2024, 0, 7 + dayOfWeek); // Jan 7, 2024 is a Sunday

	return new Intl.DateTimeFormat(getUserLocale().localeString, {
		weekday: format,
	}).format(date);
};

/**
 * Gets the week number for a given date.
 * @param date The date to get the week number for.
 * @param weekStartsOn The day of week that starts the week, defaults to Monday.
 * @returns The week number (1-53).
 */
export const getWeekNumber = (date: Date, weekStartsOn: DayOfWeek = DayOfWeek.Monday): number => {
	return getWeek(date, { weekStartsOn });
};

export const getYearNumber = (date: Date, weekStartsOn: DayOfWeek = DayOfWeek.Monday): number => {
	return getWeekYear(date, { weekStartsOn });
};

export const isMidnight = (date: Date): boolean => {
	return date.getHours() === 0 && date.getMinutes() === 0;
};
